import React from 'react';
import { motion } from 'framer-motion';
import { Search, MessageSquare, Users, MapPin, CalendarDays, Trophy } from 'lucide-react';

const FeaturesSection = () => {
  const features = [
    {
      icon: Search,
      title: 'Easy Discovery',
      description: 'Find players, crew, clubs, and venues in your area with powerful search filters.',
    },
    {
      icon: MessageSquare,
      title: 'Simple Card Posts',
      description: 'Post what you\'re looking for and let others come to you. No endless scrolling.',
    },
    {
      icon: Users,
      title: 'Build Your Network',
      description: 'Connect with like-minded sports enthusiasts and grow your community.',
    },
    {
      icon: MapPin,
      title: 'Location-Based',
      description: 'Discover opportunities near you. Filter by city, locality, or specific venues.',
    },
    {
      icon: CalendarDays,
      title: 'Event Management',
      description: 'Create and manage sports events, matches, and tournaments effortlessly.',
    },
    {
      icon: Trophy,
      title: 'Challenge & Compete',
      description: 'Challenge other clubs and players. Organize friendly matches and tournaments.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section id="features" className="py-24 relative bg-card/30" data-testid="features-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-dots opacity-20" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
            Why Playmatez?
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            Features That <span className="gradient-text">Empower</span> You
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Everything you need to find, connect, and play - all in one powerful app
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                variants={itemVariants}
                className="feature-card p-8 group hover:-translate-y-2 transition-transform duration-300"
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <Icon className="w-7 h-7 text-primary" />
                </div>

                {/* Title */}
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>

                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-primary/30 rounded-tr-lg" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="text-muted-foreground mb-6">
            Ready to elevate your sports experience?
          </p>
          <motion.a
            href="#download"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            data-testid="features-cta-btn"
          >
            Get Started Now
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
